import { useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

import { DEFAULT_MAX_ROW_WIDTH, NODE_GAP } from '../constants';
import { useEditCanvasStore } from '../store';
import { toNodeRect } from '../utils/layout/nodeLayout';

const EXCLUDED_NODE_TYPES = new Set(['expandingBoundingBox', 'imageGeneration']);

/**
 * Provides layout-aware positioning for canvas node operations.
 * Wraps the pure utilities in {@link ../utils/nodeLayout} with store and viewport access.
 * Must be used within a ReactFlow provider.
 */
export function useNodeLayout() {
  const { getViewport } = useReactFlow();

  const getContentNodeRects = useCallback(() => {
    const { nodes } = useEditCanvasStore.getState();

    return nodes.filter((n) => !EXCLUDED_NODE_TYPES.has(n.type)).map(toNodeRect);
  }, []);

  const getMaxRowWidth = useCallback((): number => {
    try {
      const viewport = getViewport();

      // Convert visible pixel width to flow coordinates (pixels / zoom)
      // so row wrapping scales with the user's current zoom level.
      // Use 75% of window width because the chat sidebar occupies roughly
      // the right quarter. Floor at DEFAULT_MAX_ROW_WIDTH so an extremely
      // zoomed-in viewport doesn't artificially constrain row width below
      // the canvas's natural extent.
      if (viewport.zoom > 0) {
        // biome-ignore lint/style/noMagicNumbers: get percentage for display
        const containerWidth = window.innerWidth * 0.75;

        return Math.max(containerWidth / viewport.zoom, DEFAULT_MAX_ROW_WIDTH);
      }
    } catch {
      // React Flow may not be fully initialized yet
    }
    return DEFAULT_MAX_ROW_WIDTH;
  }, [getViewport]);

  /**
   * Returns a zoom-scaled gap so spacing stays visually consistent.
   * NODE_GAP is in flow coordinates, which shrink on screen as zoom decreases.
   * Dividing by zoom compensates: at zoom 0.5 a 24-unit gap renders as 12px,
   * so we double it to 48 units — still 24px on screen. Clamped to NODE_GAP
   * so we never go below the baseline when zoomed in.
   */
  const getGap = useCallback((): number => {
    try {
      const viewport = getViewport();

      if (viewport.zoom > 0) {
        return Math.max(NODE_GAP / viewport.zoom, NODE_GAP);
      }
    } catch {
      // React Flow may not be fully initialized yet
    }

    return NODE_GAP;
  }, [getViewport]);

  return {
    getContentNodeRects,
    getGap,
    getMaxRowWidth,
  };
}
